/**
 * Every React Query key in the app, in one file.
 *
 * Scattered inline keys are how a mutation ends up invalidating nothing. Keys
 * are hierarchical arrays so `invalidateQueries({ queryKey: qk.meetings.all })`
 * catches every list and detail below it.
 */
export const qk = {
  session: ['session'] as const,
  meetings: {
    all: ['meetings'] as const,
    list: (params: Record<string, unknown>) => ['meetings', 'list', params] as const,
    detail: (id: string) => ['meetings', 'detail', id] as const,
    upcoming: ['meetings', 'upcoming'] as const,
    screenCapture: (id: string) => ['meetings', 'screenCapture', id] as const,
    audio: (id: string) => ['meetings', 'audio', id] as const,
  },
  actions: {
    all: ['actions'] as const,
    list: (params: Record<string, unknown>) => ['actions', 'list', params] as const,
    // Keyed on the meeting because the roster half of the answer differs per
    // meeting; 'workspace' is the members-only list with no meeting context.
    assignees: (meetingId: string | undefined) =>
      ['actions', 'assignees', meetingId ?? 'workspace'] as const,
    // The "filter by meeting" options. Not keyed on anything: it is the whole
    // list, searched in the browser.
    meetings: ['actions', 'meetings'] as const,
  },
  patrons: {
    all: ['patrons'] as const,
    list: (includeArchived: boolean) => ['patrons', 'list', includeArchived] as const,
    suggestions: ['patrons', 'suggestions'] as const,
  },
  search: (q: string, filters: Record<string, unknown>) => ['search', q, filters] as const,
  ask: {
    thread: (meetingId: string | null) => ['ask', meetingId ?? 'workspace'] as const,
  },
  analytics: {
    overview: (range: string) => ['analytics', 'overview', range] as const,
  },
  integrations: ['integrations'] as const,
  notifications: ['notifications'] as const,
  memberships: ['memberships'] as const,
  share: (token: string) => ['share', token] as const,
  authorization: {
    all: ['authorization'] as const,
    me: ['authorization', 'me'] as const,
    roles: ['authorization', 'roles'] as const,
    catalogue: ['authorization', 'permissions'] as const,
  },
  settings: {
    profile: ['settings', 'profile'] as const,
    workspace: ['settings', 'workspace'] as const,
    members: ['settings', 'members'] as const,
    rules: ['settings', 'rules'] as const,
    languages: ['settings', 'languages'] as const,
    notifications: ['settings', 'notifications'] as const,
    recording: ['settings', 'recording'] as const,
    security: ['settings', 'security'] as const,
    billing: ['settings', 'billing'] as const,
  },
  admin: {
    tenants: ['admin', 'tenants'] as const,
    audit: (filters?: unknown) =>
      filters === undefined
        ? (['admin', 'audit'] as const)
        : (['admin', 'audit', filters] as const),
    auditFacets: ['admin', 'audit', 'facets'] as const,
    vocabulary: ['admin', 'vocabulary'] as const,
    vocabularyHistory: ['admin', 'vocabulary', 'history'] as const,
  },
} as const
